import { Button, Checkbox, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import { ReactNode, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import U from '../../../felles/Utils';
import FirmNavnId from '../../../models/FirmNavnId';
import TTReportItem from '../../../models/payload/TTReportItem';
import { TaleSearchParams } from '../../../models/TalenesTale';
import { AppState } from '../../../store/rootReducer';
import StorsteEndringer, { formatChangePercent } from './StorsteEndringer';

const useStyles = makeStyles()(() => ({
  card: { backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, margin: '0 20px 12px 20px', },
  head: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 16px', },
  title: { fontSize: '1.05rem', fontWeight: 700, color: '#000', },
  tools: { display: 'flex', alignItems: 'center', gap: 10, },
  count: { color: '#777', fontSize: '.85em', whiteSpace: 'nowrap', },
  // Scrolls both ways; the header row stays in place (stickyHeader) while scrolling down or sideways.
  container: { width: '100%', maxHeight: 'calc(100vh - 330px)', minHeight: 200, overflow: 'auto', borderTop: '1px solid #e0e0e0', },
  th: { padding: '6px 12px', fontSize: '.8rem', fontWeight: 400, color: '#575655', backgroundColor: '#fff', borderBottom: '1px solid #c4c4c4', whiteSpace: 'nowrap',
    '& .MuiTableSortLabel-root.Mui-active, & .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': { fontWeight: 600, color: '#d30535', }, },
  td: { padding: '4px 12px', fontSize: '.85rem', color: '#222', borderBottom: '1px solid #e4e4e4', whiteSpace: 'nowrap', },
  num: { textAlign: 'right', },
  sub: { fontSize: '.8em', color: '#666', marginLeft: 6, },
  neg: { color: '#b3261e', },
  total: { '& td': { fontWeight: 'bold', backgroundColor: '#fafafa', }, },
  clickable: { cursor: 'pointer', },
  selected: { backgroundColor: '#FFCCCB !important', },
  // "Alle kolonner" menu
  menuPaper: { marginTop: 4, minWidth: 220, borderRadius: 8, border: '1px solid #e0e0e0', boxShadow: '0 4px 16px rgba(0,0,0,.12)', },
  menuItem: { minHeight: 32, paddingTop: 2, paddingBottom: 2, },
  menuIcon: { minWidth: '30px !important', },
  menuCheckbox: { padding: 0, color: '#8a8a8a', '&.Mui-checked': { color: '#d30535', }, '& .MuiSvgIcon-root': { fontSize: 18, }, },
  menuLabel: { fontSize: '.85rem', fontWeight: 600, color: '#222', },
  butikker: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: '0 16px 10px 16px', fontSize: '.8rem', color: '#575655', },
  butikk: { backgroundColor: '#f2f2f2', border: '1px solid #e0e0e0', borderRadius: 4, padding: '1px 8px', color: '#222', whiteSpace: 'nowrap', },
}));

interface Column {
  id: string;
  label: string;
  /** Shorter name in the "Alle kolonner" menu (the header label carries the dates). */
  menuLabel?: string;
  numeric: boolean;
  /** Value used for sorting and the total row. */
  value: (r: TTReportItem) => number | string;
  render: (r: TTReportItem) => ReactNode;
  /** Only offered when the report was run with "Vis bruttofortjeneste". */
  bf?: boolean;
}

const pctNumber = (a: number, b: number) => (b ? ((a - b) / Math.abs(b)) * 100 : 0);
// Whole kroner with a space as thousands separator, like SS12 / SS13.
const amountFormat = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 });
const bf = (nett: number, cost: number) => (cost !== 0 ? nett - cost : 0);
const bfPct = (nett: number, cost: number) => (cost !== 0 && nett !== 0 ? ((nett - cost) / nett) * 100 : 0);
const pct1 = (v: number) => v.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).replace('−', '-') + ' %';
const kr = (v: number) => (Math.round(v) !== 0 ? amountFormat.format(Math.round(v)) : '-');
const avgOrder = (nett: number, orders?: number) => (orders ? nett / orders : 0);

const storageKey = (visning: string) => 'ss10.hiddenColumns.' + visning;
const loadHidden = (visning: string): string[] => {
  try {
    const raw = localStorage.getItem(storageKey(visning));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};
const saveHidden = (visning: string, hidden: string[]) => {
  try { localStorage.setItem(storageKey(visning), JSON.stringify(hidden)); } catch { /* storage blocked - just not remembered */ }
};

// The columns that can be hidden, by id - the API's export column ids too.
const COLUMN_IDS = ['nettA', 'nettB', 'linesA', 'avgOrder', 'bfA', 'bfPctA', 'bfB', 'bfPctB', 'difference', 'percent'];

/** The columns the table shows for a visning (its "Alle kolonner" choice), for Excel / PDF: "nettA,nettB,...". */
export const shownColumns = (visning: string) => {
  const hidden = loadHidden(visning);
  return COLUMN_IDS.filter((id) => !hidden.includes(id)).join(',');
};

interface Props {
  /** "Varegruppe", "Kunde", ... - also the key the column choice is remembered under. */
  visning: string;
  title: ReactNode;
  rows: TTReportItem[];
  taleSearch: TaleSearchParams | undefined;
  /** Label of the id column, or undefined when the visning has no id column (Gruppe, Hovedgruppe, ...). */
  idLabel?: string;
  nameLabel: string;
  onRowClick?: (r: TTReportItem) => void;
  selectedId?: string;
}

// One table for every SS10 visning: column choice, search, row count, sorting, sticky header and
// a total row, with "Største endringer i kroner" above it.
const SalgsstatistikkTable = ({ visning, title, rows, taleSearch, idLabel, nameLabel, onRowClick, selectedId }: Props) => {
  const { classes, cx } = useStyles();
  const firmListFromAdmin: FirmNavnId[] = useSelector((state) => (state as AppState).userAdmin.firmListFromAdmin);
  const userJWT = useSelector((state) => (state as AppState).auth.userJWT);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState('nettA');
  const [search, setSearch] = useState('');
  const [hidden, setHidden] = useState<string[]>(() => loadHidden(visning));
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const brutto = taleSearch?.brutto ? 'Brutto' : 'Netto';
  const displayDate = taleSearch?.displayDate ?? '';
  const showBf = !!taleSearch?.bruttofortjeneste;

  const columns: Column[] = useMemo(() => {
    const cols: Column[] = [];
    if (idLabel) cols.push({ id: 'id', label: idLabel, numeric: false, value: (r) => r.id, render: (r) => r.id });
    cols.push({ id: 'name', label: nameLabel, numeric: false, value: (r) => r.name, render: (r) => r.name });
    cols.push({ id: 'nettA', label: brutto + ' i år ' + displayDate.substring(0, 17), menuLabel: brutto + ' i år', numeric: true, value: (r) => r.nettA, render: (r) => kr(r.nettA) });
    cols.push({ id: 'nettB', label: brutto + ' i fjor ' + displayDate.substring(18), menuLabel: brutto + ' i fjor', numeric: true, value: (r) => r.nettB, render: (r) => kr(r.nettB) });
    cols.push({
      id: 'linesA', label: 'Ordrelinjer', numeric: true, value: (r) => r.linesA ?? 0,
      render: (r) => <>{(r.linesA ?? 0).toLocaleString('nb-NO')}<span className={cx(classes.sub, { [classes.neg]: (r.linesA ?? 0) < (r.linesB ?? 0) })}>{formatChangePercent(r.linesA ?? 0, r.linesB ?? 0)}</span></>
    });
    cols.push({ id: 'avgOrder', label: 'Snitt pr. ordre', numeric: true, value: (r) => avgOrder(r.nettA, r.ordersA), render: (r) => kr(avgOrder(r.nettA, r.ordersA)) });
    cols.push({ id: 'bfA', label: 'Br.fortj. i år', numeric: true, bf: true, value: (r) => bf(r.nettA, r.nettAS), render: (r) => kr(bf(r.nettA, r.nettAS)) });
    cols.push({ id: 'bfPctA', label: 'BF % i år', numeric: true, bf: true, value: (r) => bfPct(r.nettA, r.nettAS), render: (r) => (r.nettAS !== 0 ? pct1(bfPct(r.nettA, r.nettAS)) : '') });
    cols.push({ id: 'bfB', label: 'Br.fortj. i fjor', numeric: true, bf: true, value: (r) => bf(r.nettB, r.nettBS), render: (r) => kr(bf(r.nettB, r.nettBS)) });
    cols.push({ id: 'bfPctB', label: 'BF % i fjor', numeric: true, bf: true, value: (r) => bfPct(r.nettB, r.nettBS), render: (r) => (r.nettBS !== 0 ? pct1(bfPct(r.nettB, r.nettBS)) : '') });
    cols.push({
      id: 'difference', label: brutto + ' diff.', numeric: true, value: (r) => r.difference,
      render: (r) => <span className={r.difference < 0 ? classes.neg : undefined}>{kr(r.difference)}</span>
    });
    cols.push({
      id: 'percent', label: '%', menuLabel: 'Endring %', numeric: true, value: (r) => pctNumber(r.nettA, r.nettB),
      render: (r) => <span className={r.difference < 0 ? classes.neg : undefined}>{formatChangePercent(r.nettA, r.nettB)}</span>
    });
    return cols.filter((c) => !c.bf || showBf);
  }, [idLabel, nameLabel, brutto, displayDate, showBf, classes, cx]);

  // The companies the report was run for (butikkId is "963" or "963,964,..."), shown above the table.
  const butikker: FirmNavnId[] = useMemo(() => {
    const known = [new FirmNavnId(Number(U.T_companyNr), userJWT?.CompanyName ?? '', 0), ...(firmListFromAdmin ?? [])];
    return String(taleSearch?.butikkId ?? '').split(',').map((s) => parseInt(s.trim(), 10)).filter((id) => id > 0)
      .map((id) => known.find((f) => Number(f.firmID) === id) ?? new FirmNavnId(id, '', 0));
  }, [taleSearch, firmListFromAdmin, userJWT]);

  // The first column (the id, or the name when there is no id) identifies the row, so it can't be hidden.
  const lockedId = columns[0]?.id;
  const visibleColumns = columns.filter((c) => c.id === lockedId || !hidden.includes(c.id));

  const toggleColumn = (id: string) => {
    if (id === lockedId) return;
    const next = hidden.includes(id) ? hidden.filter((h) => h !== id) : [...hidden, id];
    setHidden(next);
    saveHidden(visning, next);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.id ?? '').toString().toLowerCase().includes(q) || (r.name ?? '').toLowerCase().includes(q));
  }, [rows, search]);

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.id === orderBy) ?? columns[0];
    const dir = order === 'asc' ? 1 : -1;
    return filtered.map((r, i) => ({ r, i })).sort((a, b) => {
      const va = col.value(a.r);
      const vb = col.value(b.r);
      const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'nb', { numeric: true });
      return cmp !== 0 ? cmp * dir : a.i - b.i;
    }).map((x) => x.r);
  }, [filtered, columns, orderBy, order]);

  const handleSort = (id: string) => {
    if (orderBy === id) setOrder(order === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(id); setOrder('desc'); }
  };

  // Totals over the rows shown (so a search gives the total of what was found).
  const totalA = sorted.reduce((s, r) => s + (r.nettA || 0), 0);
  const totalB = sorted.reduce((s, r) => s + (r.nettB || 0), 0);
  const totalAS = sorted.reduce((s, r) => s + (r.nettAS || 0), 0);
  const totalBS = sorted.reduce((s, r) => s + (r.nettBS || 0), 0);
  const totalLinesA = sorted.reduce((s, r) => s + (r.linesA ?? 0), 0);
  const totalLinesB = sorted.reduce((s, r) => s + (r.linesB ?? 0), 0);
  const totalOrdersA = sorted.reduce((s, r) => s + (r.ordersA ?? 0), 0);
  const totalDiff = totalA - totalB;
  const totalCell = (c: Column): ReactNode => {
    switch (c.id) {
      case 'id': return '';
      case 'name': return 'Totalt:';
      case 'nettA': return kr(totalA);
      case 'nettB': return kr(totalB);
      case 'linesA': return <>{totalLinesA.toLocaleString('nb-NO')}<span className={cx(classes.sub, { [classes.neg]: totalLinesA < totalLinesB })}>{formatChangePercent(totalLinesA, totalLinesB)}</span></>;
      // An order with lines in several rows is counted once per row, so this is an approximation.
      case 'avgOrder': return kr(avgOrder(totalA, totalOrdersA));
      // BF % in the total row is weighted (total BF / total netto), not an average of the rows.
      case 'bfA': return kr(bf(totalA, totalAS));
      case 'bfPctA': return totalAS !== 0 ? pct1(bfPct(totalA, totalAS)) : '';
      case 'bfB': return kr(bf(totalB, totalBS));
      case 'bfPctB': return totalBS !== 0 ? pct1(bfPct(totalB, totalBS)) : '';
      case 'difference': return <span className={totalDiff < 0 ? classes.neg : undefined}>{kr(totalDiff)}</span>;
      case 'percent': return <span className={totalDiff < 0 ? classes.neg : undefined}>{formatChangePercent(totalA, totalB)}</span>;
      default: return '';
    }
  };

  return (
    <>
      {rows.length > 1 ? <StorsteEndringer rows={rows} showId={!!idLabel} /> : null}
      <div className={classes.card}>
        <div className={classes.head}>
          <div className={classes.title}>{title}</div>
          <div className={classes.tools}>
            <span className={classes.count}>{sorted.length === rows.length ? rows.length + ' rader' : sorted.length + ' av ' + rows.length + ' rader'}</span>
            <Button size="small" variant="outlined" color="inherit" startIcon={<ViewColumnOutlinedIcon fontSize="small" />} sx={{ textTransform: 'none', height: 32, fontSize: '0.82rem' }}
              onClick={(e) => setMenuAnchor(e.currentTarget)}>
              {visibleColumns.length < columns.length ? visibleColumns.length + ' av ' + columns.length + ' kolonner' : 'Alle kolonner'}
            </Button>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{ paper: { className: classes.menuPaper } }}>
              {columns.map((c) => {
                const locked = c.id === lockedId;
                return (
                  <MenuItem key={c.id} dense className={classes.menuItem} onClick={() => toggleColumn(c.id)} disableRipple={locked}
                    sx={locked ? { cursor: 'default', '&:hover': { backgroundColor: 'transparent' } } : undefined}>
                    <ListItemIcon className={classes.menuIcon}>
                      {locked
                        ? <LockOutlinedIcon sx={{ fontSize: 18, color: '#575655' }} />
                        : <Checkbox size="small" checked={!hidden.includes(c.id)} tabIndex={-1} disableRipple className={classes.menuCheckbox} />}
                    </ListItemIcon>
                    <ListItemText primary={c.menuLabel ?? c.label} slotProps={{ primary: { className: classes.menuLabel } }} />
                  </MenuItem>
                );
              })}
            </Menu>
            <TextField size="small" placeholder="Søk i tabellen" value={search} inputProps={{ 'aria-label': 'Søk i tabellen' }} sx={{ width: 220, '& .MuiOutlinedInput-root': { height: 32, fontSize: '0.85rem' } }} onChange={(e) => setSearch(e.target.value)}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
          </div>
        </div>
        {butikker.length > 0 ? (
          <div className={classes.butikker}>
            <span>{butikker.length === 1 ? 'Butikk:' : butikker.length + ' butikker samlet:'}</span>
            {butikker.map((f) => <span key={f.firmID} className={classes.butikk}>{(f.firmID + ' ' + f.firmName).trim()}</span>)}
          </div>
        ) : null}
        <TableContainer className={classes.container}>
          <Table stickyHeader size="small" aria-label={visning}>
            <TableHead>
              <TableRow>
                {visibleColumns.map((c) => (
                  <TableCell key={c.id} className={cx(classes.th, { [classes.num]: c.numeric })} sortDirection={orderBy === c.id ? order : false}>
                    <TableSortLabel active={orderBy === c.id} direction={orderBy === c.id ? order : 'desc'} onClick={() => handleSort(c.id)}>
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((r) => (
                // A clickable row (Kunde) can also be reached with Tab and chosen with Enter or Space.
                <TableRow hover key={r.id || r.name} onClick={onRowClick ? () => onRowClick(r) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(r); } } : undefined}
                  className={cx({ [classes.clickable]: !!onRowClick, [classes.selected]: selectedId !== undefined && selectedId === r.id })}>
                  {visibleColumns.map((c) => (
                    <TableCell key={c.id} className={cx(classes.td, { [classes.num]: c.numeric })}>{c.render(r)}</TableCell>
                  ))}
                </TableRow>
              ))}
              {sorted.length > 0 ? (
                <TableRow className={classes.total}>
                  {visibleColumns.map((c) => (
                    <TableCell key={c.id} className={cx(classes.td, { [classes.num]: c.numeric })}>{totalCell(c)}</TableCell>
                  ))}
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className={classes.td} sx={{ textAlign: 'center', color: '#777', py: 3 }}>
                    {rows.length > 0 ? `Ingen rader passer til «${search.trim()}».` : 'Ingen salg i perioden.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};

export default SalgsstatistikkTable;
